import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, Stack, TextField, Typography } from '@mui/material';
import React from 'react';
import { BLUE, BORDER, ERROR, ERROR_SOFT, ERROR_TEXT, GREEN, INTER, MUTED, NAVY, POPPINS, primaryButtonSx, SLATE, SUCCESS_SOFT, SUCCESS_TEXT, SURFACE_MUTED, SURFACE_SUBTLE, WARNING, WARNING_SOFT, WARNING_TEXT } from '../teacherStyles';
import { students } from './ClassPageData';
import { ProgressBar, SectionCard } from './ClassPageUi';

type Student = (typeof students)[number];

// The sample rows below are a readable subset of the full class roster.
const ENROLLED_COUNT = 18;

const GRID_COLUMNS = 'minmax(240px, 1.4fr) minmax(150px, .8fr) 150px 140px 96px';

const emptyStudentForm = { firstName: '', lastName: '', email: '' };

function formForStudent(student: Student) {
    const [firstName, ...rest] = student.name.split(' ');
    return { firstName, lastName: rest.join(' '), email: student.email };
}

function statusChipSx(status: string) {
    if (status === 'On track') return { color: SUCCESS_TEXT, backgroundColor: SUCCESS_SOFT };
    if (status === 'Not started') return { color: ERROR_TEXT, backgroundColor: ERROR_SOFT };
    return { color: WARNING_TEXT, backgroundColor: WARNING_SOFT };
}

function progressColor(progress: number, status: string) {
    return status === 'Invited' ? MUTED : progress < 50 ? ERROR : progress < 70 ? WARNING : GREEN;
}

const fieldSx = { '& .MuiInputBase-root, & .MuiInputLabel-root': { fontFamily: INTER }, '& .MuiFormHelperText-root': { fontFamily: INTER } };

const destructiveButtonSx = { fontFamily: INTER, fontWeight: 700, fontSize: '15px', px: 2.5, backgroundColor: ERROR, color: '#fff', boxShadow: 'none', '&:hover': { backgroundColor: ERROR_TEXT, boxShadow: 'none' } };

export default function ClassStudentsPage({ onAction }: { onAction: (message: string) => void }) {
    const [query, setQuery] = React.useState('');
    const [roster, setRoster] = React.useState(students);
    const [dialogMode, setDialogMode] = React.useState<'add' | 'edit' | null>(null);
    const [editTarget, setEditTarget] = React.useState<Student | null>(null);
    const [confirmRemoveOpen, setConfirmRemoveOpen] = React.useState(false);
    const [form, setForm] = React.useState(emptyStudentForm);

    const visibleStudents = roster.filter(student => `${student.name} ${student.email}`.toLowerCase().includes(query.trim().toLowerCase()));
    const enrolled = ENROLLED_COUNT + roster.length - students.length;
    const isEditing = dialogMode === 'edit';
    // A student who has claimed their account owns their own name and sign-in address.
    const identityLocked = isEditing && editTarget?.status !== 'Invited';
    const canSubmit = form.firstName.trim().length > 0 && form.lastName.trim().length > 0 && form.email.trim().length > 0;

    const setField = (field: keyof typeof emptyStudentForm, value: string) => setForm(current => ({ ...current, [field]: value }));

    const openAddDialog = () => {
        setForm(emptyStudentForm);
        setEditTarget(null);
        setDialogMode('add');
    };

    const openEditDialog = (student: Student) => {
        setForm(formForStudent(student));
        setEditTarget(student);
        setDialogMode('edit');
    };

    const closeDialog = () => {
        setDialogMode(null);
        setEditTarget(null);
        setForm(emptyStudentForm);
    };

    const submitDialog = () => {
        if (!canSubmit) return;
        const firstName = form.firstName.trim();
        const lastName = form.lastName.trim();
        const email = form.email.trim();
        const name = `${firstName} ${lastName}`;
        const initials = `${firstName[0]}${lastName[0]}`.toUpperCase();

        if (isEditing && editTarget) {
            setRoster(current => current.map(student => (student.email === editTarget.email ? { ...student, name, initials, email } : student)));
            onAction(`${name}'s details saved.`);
        } else {
            setRoster(current => [...current, { initials, name, email, progress: 0, lastActive: 'Not joined yet', status: 'Invited' }]);
            onAction(`Invitation sent to ${name}.`);
        }
        closeDialog();
    };

    const removeStudent = () => {
        if (!editTarget) return;
        setRoster(current => current.filter(student => student.email !== editTarget.email));
        onAction(`${editTarget.name} removed from this class.`);
        setConfirmRemoveOpen(false);
        closeDialog();
    };

    return (
        <SectionCard sx={{ overflow: 'hidden' }}>
                <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} spacing={1.5} sx={{ p: 2.5, borderBottom: `1px solid ${BORDER}` }}>
                    <Box>
                        <Typography sx={{ fontFamily: POPPINS, fontWeight: 600, fontSize: 18, color: NAVY }}>Students</Typography>
                        <Typography sx={{ fontFamily: INTER, fontSize: 12, color: SLATE, mt: 0.25 }}>{enrolled} enrolled students</Typography>
                    </Box>
                    <TextField value={query} onChange={event => setQuery(event.target.value)} size="small" placeholder="Search students" InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ color: MUTED, fontSize: 19 }} /></InputAdornment> }} sx={{ width: { xs: '100%', md: 230 }, ml: { md: 'auto !important' }, '& .MuiOutlinedInput-root': { height: 39, borderRadius: '11px', fontFamily: INTER, fontSize: 12.5 } }} />
                    <Button variant="contained" startIcon={<PersonAddAltRoundedIcon />} onClick={openAddDialog} sx={{ ...primaryButtonSx, borderRadius: '11px' }}>Add student</Button>
                </Stack>

                <Box sx={{ overflowX: 'auto' }}>
                    <Box sx={{ minWidth: 760 }}>
                        <Box sx={{ display: 'grid', gridTemplateColumns: GRID_COLUMNS, gap: 2, px: 2.5, py: 1.25, backgroundColor: SURFACE_SUBTLE }}>
                            {['Student', 'Progress', 'Last active', 'Status', ''].map(label => <Typography key={label || 'actions'} sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 11.5, color: SLATE, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Typography>)}
                        </Box>
                        {visibleStudents.map((student, index) => (
                            <Box key={student.email} sx={{ display: 'grid', gridTemplateColumns: GRID_COLUMNS, gap: 2, alignItems: 'center', px: 2.5, py: 1.5, borderTop: index === 0 ? 0 : `1px solid ${BORDER}` }}>
                                <Stack direction="row" alignItems="center" spacing={1.25}>
                                    <Avatar sx={{ width: 36, height: 36, backgroundColor: index % 2 ? SLATE : BLUE, fontFamily: POPPINS, fontSize: 11 }}>{student.initials}</Avatar>
                                    <Box><Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12.5, color: NAVY }}>{student.name}</Typography><Typography sx={{ fontFamily: INTER, fontSize: 11.5, color: SLATE, mt: 0.2 }}>{student.email}</Typography></Box>
                                </Stack>
                                <ProgressBar value={student.progress} color={progressColor(student.progress, student.status)} />
                                <Typography sx={{ fontFamily: INTER, fontSize: 11.5, color: SLATE }}>{student.lastActive}</Typography>
                                <Chip label={student.status} size="small" sx={{ justifySelf: 'start', fontFamily: INTER, fontWeight: 700, fontSize: 11, ...statusChipSx(student.status) }} />
                                <Stack direction="row" spacing={0.25} sx={{ justifySelf: 'end' }}>
                                    <IconButton size="small" title={`Edit ${student.name}`} aria-label={`Edit ${student.name}`} onClick={() => openEditDialog(student)} sx={{ color: SLATE, '&:hover': { color: BLUE } }}><EditOutlinedIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" title={`Open ${student.name}'s class progress`} aria-label={`Open ${student.name}'s class progress`} onClick={() => onAction(`${student.name}'s class progress selected.`)} sx={{ color: SLATE }}><ChevronRightRoundedIcon /></IconButton>
                                </Stack>
                            </Box>
                        ))}
                        {visibleStudents.length === 0 && <Typography sx={{ p: 3, textAlign: 'center', fontFamily: INTER, fontSize: 12.5, color: SLATE }}>No students match “{query}”.</Typography>}
                    </Box>
                </Box>

                <Dialog open={dialogMode !== null} onClose={closeDialog} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '20px' } }}>
                    <DialogTitle sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY }}>{isEditing ? 'Edit student' : 'Add a student'}</DialogTitle>
                    <DialogContent>
                        <Typography sx={{ fontFamily: INTER, fontSize: 13, lineHeight: 1.55, color: SLATE, mb: 2.5 }}>
                            {identityLocked
                                ? 'This student has joined and now manages their own name and sign-in address.'
                                : isEditing
                                  ? 'They have not joined yet, so you can still correct their name or email.'
                                  : 'They will receive an invitation to join this class. Their progress appears here once they start a lesson.'}
                        </Typography>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                            <TextField autoFocus={!identityLocked} disabled={identityLocked} fullWidth label="First name" value={form.firstName} onChange={event => setField('firstName', event.target.value)} sx={fieldSx} />
                            <TextField disabled={identityLocked} fullWidth label="Last name" value={form.lastName} onChange={event => setField('lastName', event.target.value)} sx={fieldSx} />
                        </Stack>
                        <TextField
                            fullWidth
                            type="email"
                            disabled={identityLocked}
                            label="Student email"
                            placeholder="student@example.edu"
                            value={form.email}
                            onChange={event => setField('email', event.target.value)}
                            onKeyDown={event => { if (event.key === 'Enter') submitDialog(); }}
                            helperText={identityLocked ? 'Only the student can change this.' : 'The invitation is sent here. The student signs in with this address.'}
                            sx={{ ...fieldSx, mt: 2 }}
                        />

                        {isEditing ? (
                            editTarget?.status === 'Invited' && (
                                <Box sx={{ mt: 2.5, p: 1.5, borderRadius: '11px', backgroundColor: SURFACE_MUTED }}>
                                    <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12, color: NAVY }}>Invitation not accepted yet</Typography>
                                    <Typography sx={{ fontFamily: INTER, fontSize: 11.5, lineHeight: 1.5, color: SLATE, mt: 0.4 }}>They have not set a password since the invitation was sent.</Typography>
                                    <Button onClick={() => onAction(`Invitation resent to ${editTarget.email}.`)} sx={{ mt: 0.75, px: 0, color: BLUE, fontFamily: INTER, fontWeight: 700, fontSize: 12.5, textTransform: 'none' }}>Resend invitation</Button>
                                </Box>
                            )
                        ) : (
                            <Box sx={{ mt: 2.5, p: 1.5, borderRadius: '11px', backgroundColor: SURFACE_MUTED }}>
                                <Typography sx={{ fontFamily: INTER, fontWeight: 700, fontSize: 12, color: NAVY }}>Adding a whole class?</Typography>
                                <Typography sx={{ fontFamily: INTER, fontSize: 11.5, lineHeight: 1.5, color: SLATE, mt: 0.4 }}>Share the class signup link instead and students join themselves.</Typography>
                                <Button startIcon={<ContentCopyRoundedIcon />} onClick={() => onAction('Class signup link copied.')} sx={{ mt: 0.75, px: 0, color: BLUE, fontFamily: INTER, fontWeight: 700, fontSize: 12.5, textTransform: 'none' }}>Copy signup link</Button>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        {isEditing && (
                            <Button onClick={() => setConfirmRemoveOpen(true)} sx={{ mr: 'auto', fontFamily: INTER, fontWeight: 700, color: ERROR_TEXT, textTransform: 'none', '&:hover': { backgroundColor: ERROR_SOFT } }}>Remove from class</Button>
                        )}
                        <Button onClick={closeDialog} sx={{ color: SLATE, fontFamily: INTER }}>{identityLocked ? 'Close' : 'Cancel'}</Button>
                        {!identityLocked && (
                            <Button variant="contained" disabled={!canSubmit} onClick={submitDialog} sx={{ ...primaryButtonSx, px: 2.5 }}>
                                {isEditing ? 'Save changes' : 'Send invitation'}
                            </Button>
                        )}
                    </DialogActions>
                </Dialog>

                <Dialog open={confirmRemoveOpen} onClose={() => setConfirmRemoveOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: '20px' } }}>
                    <DialogTitle sx={{ fontFamily: POPPINS, fontWeight: 600, color: NAVY }}>Remove {editTarget?.name} from this class?</DialogTitle>
                    <DialogContent>
                        <Typography sx={{ fontFamily: INTER, fontSize: 13, lineHeight: 1.55, color: SLATE }}>
                            They lose access to these lessons and stop appearing in class reports. Their completed work and speaking scores are kept, and you can add them back with the same email.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ p: 2 }}>
                        <Button onClick={() => setConfirmRemoveOpen(false)} sx={{ color: SLATE, fontFamily: INTER }}>Cancel</Button>
                        <Button variant="contained" onClick={removeStudent} sx={destructiveButtonSx}>Remove student</Button>
                    </DialogActions>
                </Dialog>
        </SectionCard>
    );
}
